import { useEffect, useReducer } from "react";
import { useDispatch } from "react-redux";
import { createAction } from "@reduxjs/toolkit";
import type { Action } from "@reduxjs/toolkit";
import {
  VendorCredit,
  SupplierVendorCredit,
  BillVendorCredit,
  PurchaseOrder,
  SalesOrder,
  PurchaseOrderItem,
  ExtraItem,
  Breakdown,
  Cost,
} from "../../types/vendor_credit";

import { saveVendorCredit } from "../../redux/vendor_credits";

type DraftBy<T, K extends keyof T> = Omit<T, K> & { [key in K]: string };

export type DraftSupplierVendorCredit = DraftBy<SupplierVendorCredit, "total">;
export type DraftExtraItem = DraftBy<ExtraItem, "quantity" | "unitCost">;
export type DraftBreakdown = DraftBy<Breakdown, "quantity" | "unitCost">;
export type DraftCost = DraftBy<Cost, "quantity" | "unitCost">;
export type DraftPurchaseOrderItem = Omit<
  PurchaseOrderItem,
  "breakdowns" | "costs"
> & {
  breakdowns: DraftBreakdown[];
  costs: DraftCost[];
};
type DraftItem = DraftExtraItem | DraftPurchaseOrderItem;
type DraftPurchaseOrder = Omit<PurchaseOrder, "items"> & {
  items: DraftItem[];
};
type DraftSalesOrder = Omit<SalesOrder, "items"> & {
  items: DraftItem[];
};
type DraftSourceForm = DraftPurchaseOrder | DraftSalesOrder;

export type DraftBillVendorCredit = Omit<BillVendorCredit, "sourceForms"> & {
  sourceForms: DraftSourceForm[];
};

type DraftVendorCredit = DraftSupplierVendorCredit | DraftBillVendorCredit;

function toDraft(vendorCredit: VendorCredit): DraftVendorCredit {
  if (vendorCredit.type === "SUPPLIER") {
    return {
      ...vendorCredit,
      total: vendorCredit.total.toFixed(2),
    };
  } else if (vendorCredit.type === "BILL") {
    return {
      ...vendorCredit,
      sourceForms: vendorCredit.sourceForms.map((form) => ({
        ...form,
        items: form.items.map((item) => {
          if ("breakdowns" in item) {
            return {
              ...item,
              breakdowns: item.breakdowns.map((b) => ({
                ...b,
                quantity: b.quantity.toFixed(0),
                unitCost: b.unitCost.toFixed(2),
              })),
              costs: item.costs.map((c) => ({
                ...c,
                quantity: c.quantity.toFixed(0),
                unitCost: c.unitCost.toFixed(2),
              })),
            };
          } else {
            return {
              ...item,
              quantity: item.quantity.toFixed(0),
              unitCost: item.unitCost.toFixed(2),
            };
          }
        }),
      })),
    };
  }
}

function fromDraft(draft: DraftVendorCredit): VendorCredit {
  if (draft.type === "SUPPLIER") {
    return {
      ...draft,
      total: Number(draft.total),
    };
  } else if (draft.type === "BILL") {
    return {
      ...draft,
      sourceForms: draft.sourceForms.map((form: DraftSourceForm) => (
        "PURCHASE-ORDER" === form.type ? {
          id: form.id,
          type: "PURCHASE-ORDER",
          number: form.number,
          division: form.division,
          destination: form.destination,
          active: form.active,
          billed: form.billed,
          project: form.project,
          items: form.items.map((item) => {
            if ("breakdowns" in item) {
              return {
                ...item,
                breakdowns: item.breakdowns.map((b) => ({
                  ...b,
                  quantity: Number(b.quantity),
                  unitCost: Number(b.unitCost),
                })),
                costs: item.costs.map((c) => ({
                  ...c,
                  quantity: Number(c.quantity),
                  unitCost: Number(c.unitCost),
                })),
              };
            } else {
              return {
                ...item,
                quantity: Number(item.quantity),
                unitCost: Number(item.unitCost),
              };
            }
          }),
        } : {
          id: form.id,
          type: "SALES-ORDER",
          number: form.number,
          active: form.active,
          project: form.project,
          items: form.items.map((item) => {
            if ("breakdowns" in item) {
              return {
                ...item,
                breakdowns: item.breakdowns.map((b) => ({
                  ...b,
                  quantity: Number(b.quantity),
                  unitCost: Number(b.unitCost),
                })),
                costs: item.costs.map((c) => ({
                  ...c,
                  quantity: Number(c.quantity),
                  unitCost: Number(c.unitCost),
                })),
              };
            } else {
              return {
                ...item,
                quantity: Number(item.quantity),
                unitCost: Number(item.unitCost),
              };
            }
          }),
        }
      )),
    };
  }
}

function isValidTotal(value: string): boolean {
  const total = Number(value);
  return (
    value !== "" && !Number.isNaN(total) && Number.isFinite(total) && total >= 0
  );
}
function isValidQuantity(value: string): boolean {
  const quantity = Number(value);
  return value !== "" && Number.isInteger(quantity) && quantity >= 0;
}

function isValidUnitCost(value: string): boolean {
  const unitCost = Number(value);
  return (
    value !== "" && !Number.isNaN(unitCost) && Number.isFinite(unitCost) && unitCost >= 0
  );
}

function isValidDraft(draft: DraftVendorCredit): boolean {
  if (draft.type === "SUPPLIER") {
    return draft.division.id !== "" && isValidTotal(draft.total);
  } else if (draft.type === "BILL") {
    const isValidBill = draft.bill.id !== "";
    const isValidSourceForms = draft.sourceForms.every((form) =>
      form.items.every((item) =>
        !("breakdowns" in item)
          ? isValidQuantity(item.quantity) && isValidUnitCost(item.unitCost)
          : item.breakdowns.every((b) => isValidQuantity(b.quantity) && isValidUnitCost(b.unitCost)) &&
          item.costs.every((c) => isValidQuantity(c.quantity)),
      ),
    );
    return (
      isValidBill &&
      isValidSourceForms
    );
  }
}

interface Division {
  division_id: string;
  division_name: string;
  default_currency_id: string;
}

interface ItemQuantityPayload {
  billItemId: string;
  quantity: string;
}

interface ItemUnitCostPayload {
  billItemId: string;
  unitCost: string;
}
const setNotes = createAction<string>("vendor-credit/set-notes");
const setTotal = createAction<string>("vendor-credit/set-total");
const setDivision = createAction<Division>("vendor-credit/set-division");
const setItemQuantity = createAction<ItemQuantityPayload>(
  "vendor-credit/set-item-quantity",
);
const setItemUnitCost = createAction<ItemUnitCostPayload>(
  "vendor-credit/set-item-unit-cost",
);
const resetDraft = createAction<VendorCredit>("vendor-credit/reset-draft");
const setTaxId = createAction<string>("vendor-credit/set-tax");

function draftReducer(
  state: DraftVendorCredit,
  action: Action,
): DraftVendorCredit {
  if (setNotes.match(action)) {
    return { ...state, notes: action.payload };
  } else if (setTotal.match(action)) {
    if ("BILL" === state.type) {
      return state;
    }
    return { ...state, total: action.payload.replaceAll(/[^0-9.]/g, '') };
  } else if (setDivision.match(action)) {
    if ("BILL" === state.type) {
      return state;
    }
    return {
      ...state,
      currency: action.payload.default_currency_id ?? "USD",
      division: {
        id: action.payload.division_id,
        name: action.payload.division_name,
      },
    };
  } else if (setItemQuantity.match(action)) {
    if ("SUPPLIER" === state.type) {
      return state;
    }
    return {
      ...state,
      sourceForms: state.sourceForms?.map((form) => ({
        ...form,
        items: form.items.map((item) =>
          "breakdowns" in item
            ? {
                ...item,
                breakdowns: item.breakdowns.map((b) =>
                  b.billItemId === action.payload.billItemId
                    ? {
                        ...b,
                        quantity: action.payload.quantity.replaceAll(/[^0-9]/g, ''),
                      }
                    : b,
                ),
                costs: item.costs.map((c) =>
                  c.billItemId === action.payload.billItemId
                    ? {
                        ...c,
                        quantity: action.payload.quantity.replaceAll(/[^0-9]/g, ''),
                      }
                    : c,
                ),
              }
            : item.billItemId === action.payload.billItemId
              ? {
                  ...item,
                  quantity: action.payload.quantity.replaceAll(/[^0-9]/g, ''),
                }
              : item,
        ),
      })),
    };
  } else if (setItemUnitCost.match(action)) {
    if ("SUPPLIER" === state.type) {
      return state;
    }
    return {
      ...state,
      sourceForms: state.sourceForms?.map((form) => ({
      ...form,
      items: form.items.map((item) =>
        "breakdowns" in item
        ? {
          ...item,
          breakdowns: item.breakdowns.map((b) =>
            b.billItemId === action.payload.billItemId
            ? {
              ...b,
                unitCost: action.payload.unitCost.replaceAll(/[^0-9.]/g, ''),
              }
            : b,
          ),
          costs: item.costs.map((c) =>
            c.billItemId === action.payload.billItemId
            ? {
              ...c,
                unitCost: action.payload.unitCost.replaceAll(/[^0-9.]/g, ''),
              }
            : c,
          ),
          }
        : item.billItemId === action.payload.billItemId
          ? {
            ...item,
              unitCost: action.payload.unitCost.replaceAll(/[^0-9.]/g, ''),
          }
          : item,
      ),
      })),
    };
  }
  else if (resetDraft.match(action)) {
    return toDraft(action.payload);
  } else if (setTaxId.match(action)) {
    if ("BILL" === state.type) {
      return state;
    }
    return { ...state, tax_id: action.payload };
  }
  return state;
}

export default function useDraft(vendorCredit: VendorCredit) {
  const dispatch = useDispatch();
  const [draft, updateDraft] = useReducer(draftReducer, vendorCredit, toDraft);

  useEffect(() => {
    updateDraft(resetDraft(vendorCredit));
  }, [vendorCredit]);

  const isNew = !vendorCredit.id;
  const isEditable = !vendorCredit.dateExported;
  const isValid = isValidDraft(draft);
  const updater = {
    setNotes: (notes: string) => updateDraft(setNotes(notes)),
    setDivision: (division: Division) => updateDraft(setDivision(division)),
    setTotal: (total: string) => updateDraft(setTotal(total)),
    setItemQuantity: (billItemId: string, quantity: string) =>
      updateDraft(setItemQuantity({ billItemId, quantity })),
    setItemUnitCost: (billItemId: string, unitCost: string) =>
      updateDraft(setItemUnitCost({ billItemId, unitCost })),
    setTaxId: (tax_id: string) => updateDraft(setTaxId(tax_id)),
  };
  const save = async () => {
    if (!isValid) {
      return;
    }
    await dispatch(saveVendorCredit(fromDraft(draft)));
  };

  return {
    draft,
    isNew,
    isEditable,
    isValid,
    updater,
    save,
  };
}
export type DraftUpdater = ReturnType<typeof useDraft>["updater"];
